// src/pages/Transactions/Transactions.jsx
import React, { useEffect, useState } from 'react';
import { auth, db } from '../../services/firebase';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import TransactionFilters from './TransactionFilters';
import TransactionCard from './TransactionCard';
import { LoadingState, EmptyState, NoUserState } from './TransactionStates';
import { transactionStyles } from './styles';

// Receipt Icon SVG
const ReceiptIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const user = auth.currentUser;

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Fetch transactions where user is buyer
        const buyerQuery = query(
          collection(db, 'transactions'),
          where('buyerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        // Fetch transactions where user is seller
        const sellerQuery = query(
          collection(db, 'transactions'),
          where('sellerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        const [buyerSnapshot, sellerSnapshot] = await Promise.all([
          getDocs(buyerQuery),
          getDocs(sellerQuery)
        ]);

        const buyerTransactions = await Promise.all(
          buyerSnapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const sellerDoc = await getDoc(doc(db, 'users', data.sellerId));
            return {
              id: docSnap.id,
              ...data,
              type: 'purchase',
              otherUser: sellerDoc.exists() ? sellerDoc.data() : null,
              totalAmount: (data.amount || 0) / 100 // Convert from cents
            };
          })
        );

        const sellerTransactions = await Promise.all(
          sellerSnapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const buyerDoc = await getDoc(doc(db, 'users', data.buyerId));
            return {
              id: docSnap.id,
              ...data,
              type: 'sale',
              otherUser: buyerDoc.exists() ? buyerDoc.data() : null,
              originalPrice: (data.amount || 0) / 100, // Original sale price
              sellerReceives: (data.sellerAmount || 0) / 100, // What seller actually gets
              platformFee: (data.platformFee || 0) / 100, // Your 5%
              stripeFee: (data.stripeProcessingFee || 0) / 100, // Stripe's fee
              totalFees: (data.totalFees || 0) / 100, // Combined fees
              // For backwards compatibility with old transaction card
              totalAmount: (data.sellerAmount || 0) / 100,
              serviceFee: (data.platformFee || 0) / 100
            };
          })
        );

        const allTransactions = [...buyerTransactions, ...sellerTransactions]
          .sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());

        setTransactions(allTransactions);
      } catch (error) {
        // If index is building, show empty state temporarily
        if (error.code === 'failed-precondition') {
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [user]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (filter === 'purchases') return transaction.type === 'purchase';
    if (filter === 'sales') return transaction.type === 'sale';
    return true;
  });

  if (!user) {
    return <NoUserState />;
  }

  return (
    <div style={transactionStyles.pageContainer}>
      <div style={transactionStyles.container}>
        <div style={transactionStyles.header}>
          <div style={transactionStyles.iconWrapper}>
            <ReceiptIcon />
          </div>
          <h1 style={transactionStyles.title}>Order History</h1>
          <p style={transactionStyles.subtitle}>
            View your purchase and sales history on Redbird Markets
          </p>
        </div>

        <TransactionFilters filter={filter} setFilter={setFilter} />

        {loading ? (
          <LoadingState />
        ) : filteredTransactions.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div style={transactionStyles.transactionsList}>
            {filteredTransactions.map((transaction) => (
              <TransactionCard 
                key={transaction.id}
                transaction={transaction}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;